import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PrefsProvider, SessionProvider, configureShell, loadPrefs, resolveTheme } from '@zehnmindai/embed/react';

import { buildInfo, sentryRelease } from './shared/buildInfo';
import { env } from './shared/utils/env';
import { DOCS_MODULE, installHosts, installModules } from './shared/utils/modules';
import { setThemePalette } from './shared/utils/colors.js';
import './index.css';
import i18n, { LANGUAGES, showLanguage } from './i18n';
import App from './App.jsx';

console.log(
  `%c Zehn Docs %c v${buildInfo.version} `,
  'background: #4C78DF; color: #fff; border-radius: 3px 0 0 3px; padding: 2px 4px; font-weight: bold;',
  'background: #324E8C; color: #fff; border-radius: 0 3px 3px 0; padding: 2px 4px;'
);

// Initialize Sentry for error tracking. It reads the pod's runtime config
// (shared/utils/env.js): on whenever a DSN is set, tagged with this deploy's
// environment and release name.
const sentryDsn = env('VITE_SENTRY_DSN');

Sentry.init({
  dsn: sentryDsn,
  environment: env('VITE_SENTRY_ENVIRONMENT') || 'development',
  release: sentryRelease,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: parseFloat(env('VITE_SENTRY_TRACES_SAMPLE_RATE')) || 1.0,
  // Session Replay
  replaysSessionSampleRate: 0.1, // 10% of sessions
  replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
  enabled: !!sentryDsn,
});

// docs in the install's shell (@zehnmindai/embed): the modules the top bar
// lists (VITE_ZEHN_MODULES), the client pages that may frame docs (its portal,
// VITE_ZEHN_HOSTS: framed there, docs shows no bar of its own and asks the
// portal to switch modules), and the domain of the person's theme and
// language cookie (zehn_prefs), which every module shares.
configureShell({
  module: DOCS_MODULE,
  modules: installModules(),
  hosts: installHosts(),
  prefsDomain: env('VITE_ZEHN_PREFS_DOMAIN') || null,
  version: buildInfo.version,
});

// Where docs kept the theme (usePersistState's "theme") and the language
// (i18next's detector, "language") before the cookie: moved into it once, so
// nobody's choice resets.
const LEGACY_PREFS_KEYS = { theme: ['theme'], language: ['language'] };

// Paint the right colours on the very first frame, in the person's theme
// (PrefsProvider keeps the palette in step after that). An intent page in
// another module's overlay takes the invoker's once connected
// (pages/intent/IntentPage.jsx).
setThemePalette(resolveTheme(loadPrefs(LEGACY_PREFS_KEYS).theme));

// The pages nobody signs in to: a guest link, a folder guest link, the QR
// verification page. They render inside the SessionProvider too (a signed-in
// person sees more on the verification page through the cookie), but nobody
// is sent to zehn-id from them.
const PUBLIC_PAGES = ['/share/', '/guest-share/', '/v/'];
const publicPage = PUBLIC_PAGES.some((prefix) => window.location.pathname.startsWith(prefix));

const render = () => createRoot(document.getElementById('root')).render(
  // The fallback renders outside every React provider (and before the i18n
  // resources may have loaded), so it reads the i18n instance directly. The
  // function form defers t() to the moment an error is actually caught.
  <Sentry.ErrorBoundary fallback={() => <p>{i18n.t('common:errorFallback')}</p>}>
    <BrowserRouter>
      {/* The person's theme and language, the same in every module (the
          zehn_prefs cookie; framed by the portal, the portal's): applied to
          the palette (and Tailwind's `dark` class) and to i18next. */}
      <PrefsProvider
        languages={LANGUAGES}
        defaultLanguage={i18n.language}
        onLanguage={showLanguage}
        onTheme={setThemePalette}
        legacyKeys={LEGACY_PREFS_KEYS}
      >
        {/* Who is signed in, from docs-api (GET /api/v1/auth/me) through
            docs' BFF (@zehnmindai/embed). Nobody: one silent sign-in at
            zehn-id, then the sign-in page (shared/auth/SessionGate.jsx). */}
        <SessionProvider silentSignIn={!publicPage}>
          <App />
        </SessionProvider>
      </PrefsProvider>
    </BrowserRouter>
  </Sentry.ErrorBoundary>
);

// An intent page opened at top level by another module's screen carries its
// request in the URL fragment. It is taken out and kept in this tab before
// anything renders: the SessionProvider may leave for zehn-id at once to sign
// the person in (pages/intent/topLevelRequest.js).
const topLevelIntentPage = (() => {
  try {
    const path = window.location.pathname;
    return window.self === window.top && (path === '/embed/sign' || path.startsWith('/embed/intent/'));
  } catch {
    return false;
  }
})();
if (topLevelIntentPage) {
  import('./pages/intent/topLevelRequest.js')
    .then((request) => request.captureTopLevelIntent())
    .catch(() => {})
    .finally(render);
} else {
  render();
}
